import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, Unauthorized } from './api';

/**
 * One loader for every screen.
 *
 * Returns `stale` data while refetching so a 10-second poll does not blank the
 * table the operator is reading. An ops console that flickers gets distrusted,
 * and a distrusted console gets ignored.
 */
export function useLoad<T>(
  fn: () => Promise<T>,
  opts: { pollMs?: number; deps?: unknown[] } = {},
) {
  const { pollMs, deps = [] } = opts;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    try {
      setData(await fn());
      setError(null);
    } catch (e) {
      if (e instanceof Unauthorized) throw e;
      setError(e instanceof ApiError ? e.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void run();
    if (!pollMs) return;
    const t = setInterval(() => void run(), pollMs);
    return () => clearInterval(t);
  }, [run, pollMs]);

  return { data, error, loading, reload: run };
}

/** The value, once it has stopped changing for `ms` — so a search box queries once, not per keystroke. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function Panel({
  title, right, children,
}: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{title}</h2>
        <div className="spacer" />
        {right}
      </div>
      {children}
    </section>
  );
}

export function Table<T>({
  rows, columns, empty, keyOf, onRow, selected,
}: {
  rows: T[] | null;
  columns: { head: string; cell: (r: T) => ReactNode; shrink?: boolean; num?: boolean }[];
  empty: string;
  keyOf: (r: T) => string;
  /** Makes rows clickable — opens the detail for that row. */
  onRow?: (r: T) => void;
  selected?: string | null;
}) {
  if (rows === null) return <div className="empty">Loading…</div>;
  if (rows.length === 0) return <div className="empty">{empty}</div>;
  return (
    <table>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.head} className={c.num ? 'num' : undefined}>{c.head}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={keyOf(r)}
            className={[onRow ? 'clickable' : '', selected === keyOf(r) ? 'selected' : ''].join(' ').trim() || undefined}
            onClick={onRow ? () => onRow(r) : undefined}
          >
            {columns.map((c) => (
              <td
                key={c.head}
                className={[c.shrink ? 'shrink' : '', c.num ? 'num' : ''].join(' ').trim() || undefined}
              >
                {c.cell(r)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type Tone = 'ok' | 'warn' | 'bad' | 'live' | 'idle';

export function State({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`state ${tone === 'idle' ? '' : tone}`}>{children}</span>;
}

/** KYC status to a tone. Anything unrecognised reads as neutral, not green. */
export function kycTone(s: string): Tone {
  if (s === 'verified') return 'ok';
  if (s === 'in_review' || s === 'pending') return 'warn';
  if (s === 'rejected' || s === 'expired') return 'bad';
  return 'idle';
}

export function tripTone(s: string): Tone {
  if (s === 'started') return 'live';
  if (s === 'completed') return 'ok';
  if (s === 'cancelled' || s === 'no_show') return 'bad';
  return 'idle';
}

export function Stat({
  label, value, unit, tone,
}: { label: string; value: ReactNode; unit?: string; tone?: 'act' | 'alarmed' }) {
  return (
    <div className={`stat${tone ? ` ${tone}` : ''}`}>
      <dt>{label}</dt>
      <dd>{value}{unit ? <small>{unit}</small> : null}</dd>
    </div>
  );
}

export function PageHead({ title, blurb }: { title: string; blurb?: string }) {
  return (
    <div className="page-head">
      <h1>{title}</h1>
      {blurb ? <p>{blurb}</p> : null}
    </div>
  );
}

export function Toast({ msg, bad }: { msg: string; bad?: boolean }) {
  return <div className={`toast${bad ? ' bad' : ''}`}>{msg}</div>;
}

/** Short ids are for eyeballing, not copying. Full id lives in the title. */
export function ShortId({ id }: { id: string }) {
  return <span className="mono" title={id}>{id.slice(0, 8)}</span>;
}

/** A Material Symbols icon (the console's icon font, loaded in index.html). */
export function Icon({ name, fill, size = 20, className }: { name: string; fill?: boolean; size?: number; className?: string }) {
  return (
    <span
      className={`ms${className ? ` ${className}` : ''}`}
      style={{ fontSize: size, fontVariationSettings: `'FILL' ${fill ? 1 : 0}` }}
      aria-hidden
    >
      {name}
    </span>
  );
}

/** A commercial number plate: yellow, IND strip, spaced as painted. */
export function Plate({ number }: { number: string }) {
  const s = number.replace(/\s+/g, '').toUpperCase();
  const m = /^([A-Z]{2})(\d{1,2})([A-Z]{0,3})(\d{1,4})$/.exec(s);
  const text = m ? [m[1], m[2], m[3], m[4]].filter(Boolean).join(' ') : number.toUpperCase();
  return <span className="plate-badge"><i>IND</i>{text}</span>;
}
